import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Loader2, Plus, FileText, MoreHorizontal, Edit3, Trash2, Check, X } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return ''; }
};

const ProjectRow = ({ project, onRename, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.name || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === project.name) { setEditing(false); return; }
    setSaving(true);
    await onRename(project.id, trimmed);
    setSaving(false);
    setEditing(false);
  };

  const del = async () => {
    setDeleting(true);
    await onDelete(project.id);
    setDeleting(false);
  };

  return (
    <li className="relative">
      <div className="block bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow" style={{ borderColor: 'var(--yb-border)' }}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                  className="font-serif text-2xl leading-snug bg-transparent border-b outline-none flex-1 min-w-0"
                  style={{ borderColor: 'var(--yb-indigo)', color: 'var(--yb-ink)' }}
                />
                <button onClick={save} disabled={saving} className="p-1.5 rounded-md" style={{ color: 'var(--yb-indigo)' }} aria-label="Save">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={16} />}
                </button>
                <button onClick={() => { setEditing(false); setValue(project.name || ''); }} className="p-1.5 rounded-md" style={{ color: 'var(--yb-muted)' }} aria-label="Cancel">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <Link to={`/projects/${project.id}`} className="block">
                <h2 className="font-serif text-2xl leading-snug" style={{ color: 'var(--yb-ink)' }}>
                  {project.name || 'Untitled project'}
                </h2>
                <p className="mt-1.5 text-[14px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>{project.prompt}</p>
                <div className="mt-3 flex items-center gap-1.5 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
                  <Calendar size={12} /> {formatDate(project.created_at)}
                </div>
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {project.generated_html ? (
              <span className="px-2 py-0.5 rounded-full text-[12px] whitespace-nowrap" style={{ backgroundColor: 'rgba(79,70,229,0.12)', color: 'var(--yb-indigo)' }}>generated</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[12px] whitespace-nowrap" style={{ backgroundColor: '#F1EFE9', color: 'var(--yb-muted)' }}>not built yet</span>
            )}
            <div className="relative">
              <button onClick={() => setMenuOpen((m) => !m)} className="p-1.5 rounded-md hover:bg-neutral-100" style={{ color: 'var(--yb-muted)' }} aria-label="Project actions">
                <MoreHorizontal size={16} />
              </button>
              {menuOpen && (
                <div
                  onMouseLeave={() => setMenuOpen(false)}
                  className="absolute right-0 top-8 z-10 w-40 bg-white rounded-xl border shadow-lg overflow-hidden"
                  style={{ borderColor: 'var(--yb-border)' }}
                >
                  <button
                    onClick={() => { setMenuOpen(false); setEditing(true); }}
                    className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 inline-flex items-center gap-2"
                    style={{ color: 'var(--yb-text)' }}
                  >
                    <Edit3 size={13} style={{ color: 'var(--yb-indigo)' }} /> Rename
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}
                    className="w-full text-left px-3 py-2 text-[13px] hover:bg-neutral-50 inline-flex items-center gap-2"
                    style={{ color: '#B4530A' }}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {confirmDelete && (
          <div className="mt-4 p-3 rounded-xl border flex items-center justify-between gap-3" style={{ borderColor: '#F1D9C4', backgroundColor: '#FDF5EE' }}>
            <p className="text-[13.5px]" style={{ color: '#7A3B03' }}>
              Delete <strong>{project.name || 'this project'}</strong>? This can’t be undone.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 rounded-full border text-[12.5px]" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
                Cancel
              </button>
              <button onClick={del} disabled={deleting} className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold">
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

const Projects = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Once we've seen an authenticated user in this mount, a subsequent
  // `user === null` (token refresh failing, network blip, etc.) should
  // surface as an inline error — NOT sign the visitor out. Auth-gate
  // redirect only happens when we've never seen a session at all.
  const hasSeenSession = useRef(false);
  // Guard against a re-fetch storm: Supabase-js emits a fresh session
  // object on every token refresh (~every hour, or on tab focus), which
  // gives `useAuth()` a new `user` object reference every time. If the
  // fetch effect depended on `user`, that would loop — refresh → new
  // user ref → effect re-runs → fetch → Supabase issues a refresh →
  // repeat. We depend on `userId` (a string) and additionally track
  // an in-flight flag so overlapping runs can't stack.
  const userId = user?.id || null;
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      if (hasSeenSession.current) {
        setError("Your session dropped. Please refresh to reload your projects.");
        setLoading(false);
        return;
      }
      navigate('/login?next=/projects', { replace: true });
      return;
    }
    hasSeenSession.current = true;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: qErr } = await supabase
          .from('user_projects')
          .select('id, name, prompt, generated_html, created_at')
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (qErr) {
          // A failed fetch is NOT the same as a signed-out session. Show
          // the message and let the user retry — don't route to /login.
          setError(qErr.message || 'Could not load your projects.');
        } else {
          setProjects(data || []);
        }
      } catch (e) {
        if (!cancelled) setError((e && e.message) || 'Could not load your projects.');
      } finally {
        fetchingRef.current = false;
        // Always drop the spinner once the fetch resolves — even for a
        // cancelled effect run. Under StrictMode the first mount is torn
        // down before completing, and if we skip setLoading here the
        // second mount (which early-returns because fetchingRef is still
        // true at its start) would leave loading=true forever.
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, navigate]);

  const handleRename = async (id, name) => {
    const { error } = await supabase.from('user_projects').update({ name }).eq('id', id);
    if (error) setError(error.message);
    else setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, name } : p)));
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from('user_projects').delete().eq('id', id);
    if (error) setError(error.message);
    else setProjects((ps) => ps.filter((p) => p.id !== id));
  };

  return (
    <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <Navbar />
      <main>
        <section className="pt-32 pb-14 lg:pt-40 lg:pb-16">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            <p className="text-[13px] uppercase tracking-widest mb-4" style={{ color: 'var(--yb-muted)' }}>Your workspace</p>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h1 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight" style={{ color: 'var(--yb-ink)' }}>
                My <span className="italic">projects.</span>
              </h1>
              <Link to="/" className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold">
                <Plus size={15} /> New project
              </Link>
            </div>
            <p className="mt-4 text-[15px]" style={{ color: 'var(--yb-muted)' }}>
              Everything you’ve started with YouBuild lives here — private to you.
            </p>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            {loading && (
              <div className="flex items-center justify-center py-20" style={{ color: 'var(--yb-muted)' }}>
                <Loader2 className="animate-spin" />
              </div>
            )}

            {!loading && error && (
              <p className="text-[14px]" style={{ color: '#B4530A' }}>{error}</p>
            )}

            {!loading && !error && projects.length === 0 && (
              <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: '#CFCDC5', backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <FileText size={22} className="mx-auto mb-3" style={{ color: 'var(--yb-indigo)' }} />
                <p className="font-serif text-2xl" style={{ color: 'var(--yb-ink)' }}>No projects yet.</p>
                <p className="mt-2 text-[14px]" style={{ color: 'var(--yb-muted)' }}>
                  Head to the homepage, describe your idea in the Build box, and it’ll land here.
                </p>
                <Link to="/" className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold mt-6">
                  Start a project
                </Link>
              </div>
            )}

            {!loading && projects.length > 0 && (
              <ul className="space-y-3">
                {projects.map((p) => (
                  <ProjectRow key={p.id} project={p} onRename={handleRename} onDelete={handleDelete} />
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Projects;
